import {
  AngleLeftLightIcon,
  EditIcon as BetaEditIcon,
  ExclamationIcon,
} from '@mm/icons';
import classnames from 'classnames';
import { words } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import useReactRouter from 'use-react-router';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import TextInput from '../../../../../common/components/TextInput';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { twillioNumberSelector } from '@mm/state/selectors/twillio';
import { DeleteButton } from '@mm/components';
import { colors } from '../../../beta/styles/colors';
import { NeedHelpLabel } from '../../../common/components/NeedHelpLabel/NeedHelpLabel';
import './EditSMSKeywordHeader.pcss';

type Props = {
  chatbotId: number;
  onDelete: () => void;
  name: string;
  onNameChange: (name: string, setPrevName: () => void) => void;
  nameEditMode: boolean;
  setNameEditMode: (editName: boolean) => void;
};

const reservedPhrases = ['start', 'stop', 'help'];

export const EditSMSKeywordHeader = ({
  chatbotId,
  onDelete,
  name,
  onNameChange,
  nameEditMode,
  setNameEditMode,
}: Props) => {
  const { history } = useReactRouter();
  const [localName, setLocalName] = useState(name);
  const [errorState, setErrorState] = useState(false);
  const twilioNumber = useSelector(twillioNumberSelector);
  const { openGeneralTextModal } = useModalActions();

  const onClickSaveName = async () => {
    if (localName !== name) {
      const isReservedPhrases = reservedPhrases
        .map(p => p.toLowerCase())
        .includes(localName.toLowerCase());
      if (isReservedPhrases) {
        return setErrorState(true);
      }

      setErrorState(false);
      try {
        await onNameChange(localName, () => setLocalName(name));
        setNameEditMode(false);
      } catch (e) {
        // Error messages will be showed on the modal
      }
    } else {
      setNameEditMode(false);
    }
  };

  const onClickCancelSaveName = () => {
    setLocalName(name);
    setNameEditMode(false);
    setErrorState(false);
  };

  const onPreviewButtonClick = () => {
    openGeneralTextModal({
      header: 'Keyword Preview',
      message: `Text ${name || ''} to ${
        twilioNumber?.number
      } to preview your keyword!`,
      closeButtonText: `Done`,
    });
  };

  return (
    <div>
      <div className="edit-sms-keyword-header__title-area-container">
        <div
          className="edit-sms-keyword-header__back-button"
          onClick={() =>
            history.push(
              BotBuilderRoutes.chatStarters.getLink({
                chatbotId,
                kind: 'sms_keywords',
              }),
            )
          }
        >
          <AngleLeftLightIcon />
          Back
        </div>
        <div className="edit-sms-keyword-header__title-area">
          <div className="edit-sms-keyword-header__title-area-left">
            {nameEditMode ? (
              <TextInput
                value={localName}
                onChange={e => {
                  setLocalName(words(e.target.value).join('').toUpperCase());
                }}
                disabled={!nameEditMode}
                className={classnames('edit-sms-keyword-header__name-input', {
                  'edit-sms-keyword-header--name-disabled-mode': !nameEditMode,
                })}
                maxLength={50}
                autoFocus
              />
            ) : (
              <div
                className="edit-sms-keyword-header__name-preview"
                onClick={() => setNameEditMode(true)}
              >
                {localName}
              </div>
            )}

            {!nameEditMode && (
              <BetaEditIcon
                className="edit-sms-keyword-header__edit-icon"
                onClick={() => setNameEditMode(true)}
              />
            )}

            {nameEditMode && (
              <>
                <PrimaryButton
                  color={ButtonColor.invertedRed}
                  onClick={onClickCancelSaveName}
                  className="edit-sms-keyword-header__name-save-button"
                >
                  Cancel
                </PrimaryButton>

                <PrimaryButton
                  onClick={onClickSaveName}
                  className={classnames(
                    'edit-sms-keyword-header__name-save-button',
                    {
                      'edit-sms-keyword-header--save-disabled': !localName,
                    },
                  )}
                >
                  Save
                </PrimaryButton>
              </>
            )}
            {errorState && (
              <div className="edit-sms-keyword-header__error-state">
                <ExclamationIcon color={colors.danger} />
                Keywords START, STOP, and HELP are reserved. Please use a
                different keyword.
              </div>
            )}
          </div>

          <div className="edit-sms-keyword-header__title-area-right">
            <NeedHelpLabel link="https://customers.ai/help/article/vjacfu0djz-sms-keywords" />
            <DeleteButton onClick={onDelete} size="lg" />

            <PrimaryButton
              color={ButtonColor.secondaryBlue}
              className="edit-sms-keyword-header__preview-button"
              onClick={onPreviewButtonClick}
              size="lg"
            >
              Preview
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};
